import { CellValueChangedEvent, StatusPanelDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';

import { AgGridTable } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { AgGridDataRow } from 'core/types';
import { useComponentTemplateActions, useGetComponentTemplates } from 'elements/hooks';
import { IComponentTemplate, IElementTemplate } from 'elements/types';

import { AddLineButton } from './AddLine';
import { columnDef } from './columns';
import { PartListColId } from './enums';
import { mapQuantityChange, mapUomChange } from './mappers';
import { PartDetailsDrawer } from './PartDetailsDrawer';
import { RemoveButton } from './selectionActions';
import { TotalsStatusBar } from './TotalsStatusBar';

interface IProps {
  elementTemplate: IElementTemplate;
}

export const PartList = ({ elementTemplate }: IProps) => {
  const [selectedPart, setSelectedPart] = useState<IComponentTemplate | null>(null);

  const { records, isLoading, loadingPercentage, currentPage } = useGetComponentTemplates({
    viewBy: { elementTemplateId: elementTemplate.id },
  });

  const { upsertComponentTemplateAssociation, upsertComponentTemplate } =
    useComponentTemplateActions();

  const columnDefs = useMemo(() => columnDef({ onPartNumberClick: setSelectedPart }), []);

  const onCellValueChanged = useCallback(
    async (
      _: AgGridReact,
      event: CellValueChangedEvent<IComponentTemplate>
    ): Promise<{ updatedRow?: AgGridDataRow }> => {
      switch (event.colDef.field) {
        case PartListColId.QUANTITY: {
          const { success, error, elementComponentTemplateAssociation } =
            await upsertComponentTemplateAssociation(mapQuantityChange(event.newValue, event.data));

          if (!success) {
            throw new Error(error);
          }

          return { updatedRow: elementComponentTemplateAssociation };
        }

        case PartListColId.UOM: {
          const {
            success,
            error,
            componentTemplate: updatedPart,
          } = await upsertComponentTemplate({
            ...mapUomChange(event.newValue, event.data),
            viewBy: { elementTemplateId: elementTemplate.id },
          });

          if (!success) {
            throw new Error(error);
          }

          return { updatedRow: updatedPart };
        }

        default:
          return {};
      }
    },
    [upsertComponentTemplateAssociation, upsertComponentTemplate, elementTemplate.id]
  );

  const defaultColDef = useMemo(() => ({ autoHeight: true }), []);

  const statusBar = useMemo<{ statusPanels: StatusPanelDef[] }>(
    () => ({
      statusPanels: [
        {
          statusPanel: TotalsStatusBar,
          align: 'left',
        },
      ],
    }),
    []
  );

  const actionButtons = useCallback(
    () => [<AddLineButton key="addLineButton" elementTemplate={elementTemplate} />],
    [elementTemplate]
  );

  const selectionActionButtons = useCallback(() => [<RemoveButton key="removeButton" />], []);

  return (
    <AgGridClientTableProvider
      tableId={`partList-${elementTemplate.id}`}
      entityType="Part"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      data={records}
      isDataLoading={isLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      statusBar={statusBar}
      onCellValueChanged={onCellValueChanged}
      layoutType={TableLayoutType.ELEMENT_TEMPLATE_PART}
      hasLayoutActions
    >
      <AgGridTable actionButtons={actionButtons} selectionActionButtons={selectionActionButtons} />

      <PartDetailsDrawer
        part={selectedPart}
        isOpen={!!selectedPart}
        onClose={() => setSelectedPart(null)}
      />
    </AgGridClientTableProvider>
  );
};
